import { useState, useEffect, useCallback } from 'react';
import logger from 'ws-scripts/modules/logger';
import { useLocationsDataReady } from './useLocationsDataReady';
import getDisplayLocations from '../utils/getDisplayLocations';

const LOG_PREFIX = '[ws-locations-control][useLocationsData]';

/**
 * Hook to integrate with ws-locations-data widget.
 * Uses the global window.DDC.LocationsData API to:
 * 1. Listen for LocationsData availability via custom event
 * 2. Subscribe to location updates via registerLocationsNotification
 * 3. Trigger location searches via setSearchParams
 */
export const useLocationsData = () => {
	const [locations, setLocations] = useState([]);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [currentPostalCode, setCurrentPostalCode] = useState(null);

	// hook to check if LocationsData is ready
	const { isReady, error: readyError } = useLocationsDataReady();

	// Set error if LocationsData failed to initialize
	useEffect(() => {
		if (readyError) {
			setError(readyError);
			setIsLoading(false);
		}
	}, [readyError]);

	// Subscribe to location updates
	useEffect(() => {
		if (!isReady) {
			logger.debug(`${LOG_PREFIX} Not subscribing - isReady: ${isReady}`);
			return () => {};
		}

		const locationsData = window.DDC.LocationsData;

		const handleLocationsUpdate = () => {
			logger.debug(`${LOG_PREFIX} Received locations update`);

			const newLocations = getDisplayLocations(locationsData);
			const newError = locationsData.getError();

			setLocations(newLocations);
			setError(newError);
			setIsLoading(false);

			logger.debug(
				`${LOG_PREFIX} Updated state - count: ${
					newLocations.length
				}, hasError: ${!!newError}`
			);
		};

		/**
		 * When a make filter is active, the visible tile count changes.
		 * Update the displayed count to match getFilteredLocations() so that
		 * the "N locations near you" label stays accurate.
		 */
		const handleFilteredLocationsUpdate = () => {
			logger.debug(`${LOG_PREFIX} Received filtered-locations update`);

			const filtered = getDisplayLocations(locationsData);
			const newError = locationsData.getError();

			setLocations(filtered);
			setError(newError);
			setIsLoading(false);

			logger.debug(
				`${LOG_PREFIX} Updated state (filtered) - count: ${filtered.length}`
			);
		};

		// Register for base location notifications
		logger.debug(`${LOG_PREFIX} Registering for location notifications`);
		const unsubscribe = locationsData.registerLocationsNotification(
			handleLocationsUpdate
		);

		// Register for filtered location notifications (make filter changes)
		const unsubscribeFiltered =
			locationsData.registerFilteredLocationsNotification(
				handleFilteredLocationsUpdate
			);

		// Cleanup subscriptions on unmount
		return () => {
			logger.debug(`${LOG_PREFIX} Unsubscribing from notifications`);
			if (unsubscribe) {
				unsubscribe();
			}
			if (unsubscribeFiltered) {
				unsubscribeFiltered();
			}
		};
	}, [isReady]);

	/**
	 * Trigger a location search via ws-locations-data.
	 *
	 * @param {Object} params
	 * @param {string|null} params.postalCode - Postal code to search around (null falls back to default).
	 * @param {number} params.range - Search radius in miles.
	 * @param {boolean} params.excludeByRange - Whether to exclude results outside the range.
	 * @param {boolean} [params.useDefaultTileCount=false] - When true, the downstream widget
	 *   uses its default tile count (i.e. no user-initiated search has occurred yet).
	 */
	const setSearchParams = useCallback(
		({
			postalCode,
			range,
			excludeByRange,
			useDefaultTileCount = false
		}) => {
			if (!isReady || !window.DDC?.LocationsData) {
				logger.warn(
					`${LOG_PREFIX} Cannot set postal code - LocationsData not ready`
				);
				return;
			}

			logger.debug(`${LOG_PREFIX} Setting postal code: ${postalCode}`, {
				range,
				excludeByRange
			});
			setIsLoading(true);
			setError(null);
			setCurrentPostalCode(postalCode);
			window.DDC.LocationsData.setSearchParams(
				postalCode,
				range,
				excludeByRange,
				useDefaultTileCount
			);
		},
		[isReady]
	);

	return {
		locations,
		locationCount: locations.length,
		error,
		isReady,
		isLoading,
		currentPostalCode,
		setSearchParams
	};
};
